<template>
  <div class="i-jca-coaching-upsell">
    <div class="card">
      <div class="top">
        <div class="header">
          <div class="badge">Premium Upgrade</div>
          <strong>Want More Personal Attention?</strong>
        </div>
        <div class="sub">Upgrade to 1:1 coaching for tailored support that moves at your pace</div>
      </div>

      <div class="main">
        <div class="left">
          <strong>Perfect if you need:</strong>
          <div class="perks">
            <div
              v-for="perk in PERKS"
              :key="perk"
              class="perk"
            >
              <img :src="iconCheck" />
              <span v-html="perk"></span>
            </div>
          </div>
        </div>
        <div class="right">
          <strong>What's Included:</strong>
          <div class="included">
            <div
              v-for="(item, index) in included"
              :key="index"
              class="item"
            >
              <span v-html="item.main"></span>
              <strong
                class="extra"
                v-html="item.extra"
              ></strong>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom">
        <div>We'll match you with the perfect coach based on your industry, goals, and experience level.</div>
        <div class="action">
          <iBaseButton
            v-bind="BUTTON_STYLE"
            @clicked="linkToCoachSelection"
          />
          <div class="note">You'll keep access to group sessions too</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { bluePurpleGradient, white } from "@/styles/colors"

import linkToCoachSelection from "@/composables/linkToCoachSelection"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconCoaching from "@/assets/icons/icon-coaching-white.svg"
import iconCheck from "@/assets/icons/icon-circle-check-green.svg"

// CONSTANTS
const PERKS = [
  "<strong>Personalized support</strong> from a professional coach",
  "<strong>Faster timeline</strong> to land your next role",
  "Interview prep for specific companies",
  "Salary negotiation support and strategy"
]
const BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "fit-content",
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  textColor: white,
  text: "Match Me With a Coach",
  preIcon: iconCoaching
}

// COMPUTED
const included = computed(() => {
  return [
    { main: "1:1 Career Coaching", extra: `Up to 2 hours/month` },
    { main: "Dedicated Career Coach", extra: "Personalized" },
    { main: "Step-by-step Guidance" },
    { main: "Ongoing Support" }
  ]
})
</script>

<style scoped lang="scss">
.i-jca-coaching-upsell {
  @include border-radius-md;

  background-image: $blue-purple-gradient;
  padding: 2px;

  .card {
    @include flex(column, start, stretch, $gap: 25px);
    @include text-sm;

    position: relative;
    border-radius: 10.5px;
    background: linear-gradient(180deg, #fff 0%, #eae7ff 100%);
    padding: 16px;
    text-align: center;

    @media screen and (min-width: $breakpoint-xs) {
      padding: 20px;
    }

    strong {
      font-weight: 600;
    }

    .top {
      .header {
        @include flex(column, start, center, $gap: 16px);
        @include text-xl;

        .badge {
          @include border-radius-sm;
          @include text-xxs;

          width: fit-content;
          color: $white;
          background-image: $blue-purple-gradient;
          font-weight: 600;
          padding: 3px 8px;

          @media screen and (min-width: $breakpoint-md) {
            position: absolute;
            top: 8px;
            right: 8px;
          }
        }
      }

      .sub {
        @include text-md;

        color: $comet700;
      }
    }

    .main {
      @include flex(column, start, stretch, $gap: 14px);

      text-align: start;

      @media screen and (min-width: $breakpoint-md) {
        @include grid($cols: repeat(2, 1fr), $rows: 1fr, $align-items: center, $gap: 20px);
      }

      @media screen and (min-width: $breakpoint-lg) {
        gap: 70px;
      }

      .left {
        @include flex(column, start, stretch, $gap: 6px);

        .perks {
          @include flex(column, start, stretch, $gap: 4px);

          color: $comet700;

          .perk {
            @include flex(row, start, center, $gap: 5px);
          }
        }
      }

      .right {
        @include flex(column, start, stretch, $gap: 6px);
        @include border-radius-sm;

        background-color: white;
        border: 1px solid $black200;
        padding: 10px;

        @media screen and (min-width: $breakpoint-xs) {
          padding: 15px;
        }

        .included {
          @include flex(column, start, stretch, $gap: 4px);

          color: $comet700;

          .item {
            @include flex(row, space-between, center, wrap, 0 2px);

            @media screen and (min-width: $breakpoint-xs) {
              gap: 5px;
            }

            .extra {
              @include text-xxs;

              @media screen and (min-width: $breakpoint-sm) {
                @include text-xs;
              }
            }
          }
        }
      }
    }

    .bottom {
      @include flex(column, start, center, $gap: 15px);

      color: $comet700;

      .action {
        @include flex(column, start, center, $gap: 6px);

        .note {
          @include text-xxs;
        }
      }
    }
  }
}
</style>
